export const navigationCopy = {
  en: {
    company: "Company",
    vendorProduct: "Vendor & Product",
    technologies: "Technologies",
    recruit: "Recruit",
    contact: "Contact",
  },
  ko: {
    company: "회사소개",
    vendorProduct: "협력사 및 제품",
    technologies: "기술 영역",
    recruit: "채용",
    contact: "문의",
  },
};

export const landingCopy = {
  en: {
    hero: {
      brandChip: "EPLUS · SEMICONDUCTOR DISTRIBUTION",
      sinceLine: "Since 1997",
      networkLine: "Global Vendor Network",
      title: "Eplus",
      subtitleLine: "Smart choice for future",
      subtitle:
        "SEMICONDUCTOR DISTRIBUTION, EMBEDDED DESIGN SUPPORT, AND PRACTICAL MARKET EXECUTION FOR AUTOMOTIVE, TELEMATICS, MOBILE, SECURITY, MEDICAL, AND MULTIMEDIA SYSTEMS.",
      ctaPrimary: "View Technologies",
      ctaSecondary: "Explore Overview",
      note1: "DISTRIBUTION + TECHNICAL SUPPORT + FORECAST + DELIVERY",
      note2: "BRIDGING CUSTOMERS, VENDORS, AND SYSTEM DESIGN",
      cardHint: "CLICK CARD TO OPEN EPLUS SNAPSHOT",
      focusKicker: "Eplus Snapshot",
      focusTitle:
        "Distribution, design support, and partner coordination in one operating layer.",
      focusCopy:
        "Eplus supports customers with semiconductor sourcing, embedded solution guidance, timely market feedback, and practical communication between customer and vendor teams.",
      focusActions: [
        "Since 1997",
        "End-to-End Support",
        "Customer + Vendor Bridge",
      ],
    },
    sections: {
      overview: {
        kicker: "Overview",
        title: "Built on Semiconductor Distribution Expertise.",
        copy: "Eplus is a semiconductor distribution and embedded solution partner founded to connect design, supply, and execution for customers. Since 1997, we have supported clients across automotive, telematics, mobile, security, medical, and multimedia systems with practical sourcing, system design support, and responsive market timing guidance. We focus on professional communication between customers and vendors, so projects move faster from planning to execution.",
        button: "Learn More About Eplus",
      },
      partnerNetwork: {
        kicker: "Partner Network",
        title: "A practical bridge between customers and vendors.",
        operatingSignals: "Operating Signals",
        copy: "This section follows a practical operating flow: sourcing, design support, demand planning, and communication are connected in one sequence, so opportunities are not lost as projects move from planning to execution.",
        button: "View Vendor Products",
      },
      applicationCoverage: {
        kicker: "Application Coverage",
        title: "Applied across real embedded environments.",
        copy: "This section maps where our capabilities are used: from automotive and telematics platforms to mobile, security, medical, and multimedia systems, showing how sourcing, design alignment, and execution readiness work together in each scenario.",
        button: "Explore Technologies",
      },
      workflow: {
        leftKicker: "Workflow",
        leftTitle: "A business flow that feels connected.",
        rightKicker: "Why Eplus",
      },
      nextSection: {
        kicker: "Contact Layer",
        title: "Contact is clear and direct.",
        copy: "After checking the company information, you can move directly to Contact Us or to the Technologies and Vendor & Product pages to review what you need.",
      },
      nextMove: {
        kicker: "Next Move",
        paragraph:
          "You can access the company overview, technology pages, and Contact Us from the buttons below.",
        about: "About Eplus",
        technologies: "Technology Pages",
        contact: "Contact Us",
      },
      designIntent: {
        kicker: "Design Intent",
        paragraph:
          "EPLUS is a business partner built on semiconductor distribution and embedded-solution support, helping customers connect vendor capabilities with real implementation. It was established to deliver practical sourcing, technical guidance, and reliable communication across automotive, telematics, mobile, security, medical, and multimedia environments.",
      },
    },
    cards: {
      metrics: [
        {
          id: "founded",
          label: "Founded",
          value: "1997",
          detail: "Long-term semiconductor distribution partner in Korea.",
        },
        {
          id: "response",
          label: "Response",
          value: "24H",
          detail: "Professional communication and practical follow-up speed.",
        },
        {
          id: "coverage",
          label: "Coverage",
          value: "6+",
          detail:
            "Automotive, telematics, mobile, medical, security, multimedia.",
        },
        {
          id: "support",
          label: "Support",
          value: "END-TO-END",
          detail:
            "Technical guidance, sourcing, forecast, delivery coordination.",
        },
      ],
      directionCards: [
        {
          id: "direction-01",
          eyebrow: "01",
          title: "Supply Technology Value-Added Solutions",
          body: "Eplus works with customers for optimum solutions, supports system designers with practical technical guidance, and introduces high-tech products at the right time.",
        },
        {
          id: "direction-02",
          eyebrow: "02",
          title: "Offer Specific Distribution for Particular Marketplaces",
          body: "The company focuses on multimedia, communication, and embedded industries with differentiated customer service and sharp understanding of market needs.",
        },
        {
          id: "direction-03",
          eyebrow: "03",
          title: "Provide Accurate Forecasts",
          body: "Forecast follow-up, on-time delivery, and frequent market survey feedback help keep customer programs stable and predictable.",
        },
        {
          id: "direction-04",
          eyebrow: "04",
          title: "Establish Professional Communications",
          body: "Eplus strengthens accessibility between customers and vendors and responds quickly with professional, actionable feedback.",
        },
      ],
      partnerSignals: [
        "OmniVision",
        "Goodix",
        "Syncomm",
        "Quectel",
        "Ikotek",
        "Embedded Solutions",
        "Technical Support",
        "Market Feedback",
      ],
      operatingSignals: [
        {
          id: "signal-01",
          title: "Market Understanding",
          body: "Good understanding of international business practice and local customer needs improves product fit and timing.",
        },
        {
          id: "signal-02",
          title: "Professional Interface",
          body: "Eplus focuses on clear communication between customers and vendors rather than leaving sourcing and design issues disconnected.",
        },
        {
          id: "signal-03",
          title: "Long-Term Collaboration",
          body: "Resources are invested toward stable, continuing relationships rather than one-off transactions.",
        },
      ],
      applicationCards: [
        {
          id: "application-01",
          code: "A1",
          title: "Automotive",
          body: "Camera, ADAS, connectivity, and vehicle intelligence platforms that require stable imaging and embedded integration support.",
        },
        {
          id: "application-02",
          code: "A2",
          title: "Telematics",
          body: "Wireless connectivity and remote field communication for fleet, tracking, and device intelligence workflows.",
        },
        {
          id: "application-03",
          code: "A3",
          title: "Mobile",
          body: "High-efficiency components and embedded modules for compact, high-performance connected products.",
        },
        {
          id: "application-04",
          code: "A4",
          title: "Medical",
          body: "Reliable device integration for medical electronics where signal stability and support responsiveness matter.",
        },
        {
          id: "application-05",
          code: "A5",
          title: "Security",
          body: "Vision, sensing, and communication components aligned with surveillance, access, and safety systems.",
        },
        {
          id: "application-06",
          code: "A6",
          title: "Multimedia",
          body: "Component sourcing and embedded support for digital appliance and multimedia system environments.",
        },
      ],
      workflowSteps: [
        {
          id: "step-01",
          step: "01",
          title: "Understand the Program",
          body: "Customer requirements, application context, and timeline are aligned before sourcing begins.",
        },
        {
          id: "step-02",
          step: "02",
          title: "Match Vendor and Solution",
          body: "Eplus bridges the right vendor, product family, and embedded direction for the target use case.",
        },
        {
          id: "step-03",
          step: "03",
          title: "Support Design and Forecast",
          body: "Technical questions, forecast follow-up, and market feedback are managed in one connected workflow.",
        },
        {
          id: "step-04",
          step: "04",
          title: "Deliver with Continuity",
          body: "Delivery timing, communication quality, and long-term relationship management complete the cycle.",
        },
      ],
      highlights: [
        {
          id: "highlight-01",
          title: "Experience-Driven Team",
          body: "Sales and engineering capability are framed as working strength, not decorative messaging.",
        },
        {
          id: "highlight-02",
          title: "Forecast and Delivery Discipline",
          body: "Timing and coordination matter as much as product selection in real customer programs.",
        },
        {
          id: "highlight-03",
          title: "Embedded Market Perspective",
          body: "The design now reflects Eplus as a solution partner inside embedded and communication-driven industries.",
        },
      ],
      contactCards: [
        {
          id: "contact-01",
          label: "Office",
          value:
            "Suite 708, Woolim W-City, 9-22 Pangyo-ro 255beon-gil, Bundang-gu, Seongnam-si, Gyeonggi-do",
        },
        {
          id: "contact-02",
          label: "Phone",
          value: "031-628-8600",
        },
        {
          id: "contact-03",
          label: "Fax",
          value: "031-628-8601",
        },
        {
          id: "contact-04",
          label: "Mail",
          value: "support@eplus.co.kr",
        },
      ],
      actionButtons: {
        joinTeam: "Join Our Team",
        about: "About Eplus",
        technologies: "Technology Pages",
        contact: "Contact Us",
      },
      footer: {
        brandDesc:
          "Since 1997, Eplus has been a trusted semiconductor distribution partner delivering embedded solutions, technical guidance, and market insight across Korea.",
        companyLinksTitle: "Company",
        supportLinksTitle: "Support",
        officeTitle: "Office",
        aboutEplus: "About Eplus",
        technologies: "Technologies",
        vendorProducts: "Vendor Products",
        careers: "Careers",
        contactUs: "Contact Us",
        supportPhone: "031-628-8600",
        supportEmail: "support@eplus.co.kr",
        fax: "Fax: 031-628-8601",
        address:
          "Suite 708, Woolim W-City, 9-22 Pangyo-ro 255beon-gil, Bundang-gu, Seongnam-si, Gyeonggi-do",
        copyright: "Copyright © 1997 EPLUS CO., LTD. All rights reserved.",
        tagline: "Semiconductor Distribution + Embedded Solutions",
      },
    },
  },
  ko: {
    hero: {
      brandChip: "EPLUS · 반도체 유통",
      sinceLine: "1997년부터",
      networkLine: "글로벌 벤더 네트워크",
      title: "이프러스",
      subtitleLine: "미래를 위한 스마트한 선택",
      subtitle:
        "반도체 유통, 임베디드 설계 지원, 그리고 자동차, 텔레매틱스, 모바일, 보안, 의료, 멀티미디어 분야의 실전형 시장 실행력.",
      ctaPrimary: "기술 확인",
      ctaSecondary: "개요 보기",
      note1: "유통 + 기술 지원 + 수요 예측 + 납기 관리",
      note2: "고객, 벤더, 시스템 설계를 연결",
      cardHint: "카드를 클릭해 Eplus Snapshot 보기",
      focusKicker: "Eplus 스냅샷",
      focusTitle: "유통, 설계 지원, 파트너 협업이 하나로 동작하는 운영 레이어.",
      focusCopy:
        "이프러스는 반도체 소싱, 임베디드 솔루션 가이드, 시장 피드백 전달, 고객·벤더 간 실무형 커뮤니케이션을 통해 고객 니즈를 맞춥니다.",
      focusActions: ["1997년부터", "엔드투엔드 지원", "고객-벤더 연결"],
    },
    sections: {
      overview: {
        kicker: "개요",
        title: "반도체 유통과 임베디드 지원을 결합한 파트너십",
        copy: "이프러스는 반도체 소싱, 임베디드 솔루션 지원, 시장 타이밍 대응, 고객과 벤더 간 전문 커뮤니케이션을 한 축으로 연결하는 파트너입니다. 1997년부터 자동차, 텔레매틱스, 모바일, 보안, 의료, 멀티미디어 등 다양한 임베디드 시장에서 실무형 실행력을 바탕으로 고객 사업의 초기 설계부터 납기와 채택까지 함께합니다.",
        button: "이프러스 더 알아보기",
      },
      partnerNetwork: {
        kicker: "파트너 네트워크",
        title: "고객과 벤더를 잇는 실무형 다리.",
        operatingSignals: "운영 시그널",
        copy: "기능 소개를 나열하는 방식이 아니라 운영 동선 기준으로 구성했습니다. 소싱·설계지원·수요예측·커뮤니케이션을 하나의 실행 흐름으로 묶어 단계 간 공백 없이 연결합니다.",
        button: "협력사 제품 보기",
      },
      applicationCoverage: {
        kicker: "적용 영역",
        title: "실제 임베디드 환경에서 사용되는 적용 사례.",
        copy: "자동차, 텔레매틱스, 모바일, 보안, 의료, 멀티미디어 등 주요 임베디드 분야에서 반도체 선택·설계 반영·실행 준비까지 이어지는 과정을 보여줍니다.",
        button: "기술 보기",
      },
      workflow: {
        leftKicker: "업무 흐름",
        leftTitle: "연결된 프로세스로 만든 업무 흐름.",
        rightKicker: "Why Eplus",
      },
      nextSection: {
        kicker: "연락처",
        title: "연결은 빠르고 명확해야 합니다.",
        copy: "이프러스 회사 안내와 함께, 문의하기 및 기술/협력사 페이지로 바로 이동해 필요한 정보를 확인할 수 있습니다.",
      },
      nextMove: {
        kicker: "다음 단계",
        paragraph:
          "회사 소개, 기술 페이지, 문의하기는 아래 버튼을 통해 확인하실 수 있습니다.",
        about: "회사 소개",
        technologies: "기술 페이지",
        contact: "문의하기",
      },
      designIntent: {
        kicker: "디자인 의도",
        paragraph:
          "이프러스는 반도체 유통과 임베디드 솔루션 지원을 결합해 고객의 사업 기획·개발·양산 단계에 실무형으로 기여하는 파트너입니다. 1997년부터 자동차·텔레매틱스·모바일·보안·의료·멀티미디어 등 현장 중심 시장에서 벤더 역량을 연결하고, 빠른 의사결정을 돕는 실행형 커뮤니케이션을 제공합니다.",
      },
    },
    cards: {
      metrics: [
        {
          id: "founded",
          label: "설립",
          value: "1997",
          detail: "국내 반도체 유통 파트너로 장기적으로 협력한 경험.",
        },
        {
          id: "response",
          label: "응답성",
          value: "24H",
          detail: "전문적인 커뮤니케이션과 실무형 후속 대응 속도.",
        },
        {
          id: "coverage",
          label: "영역",
          value: "6+",
          detail: "자동차, 텔레매틱스, 모바일, 의료, 보안, 멀티미디어.",
        },
        {
          id: "support",
          label: "지원",
          value: "END-TO-END",
          detail: "기술 가이드, 소싱, 수요 예측, 납기 조율까지 일괄 지원.",
        },
      ],
      directionCards: [
        {
          id: "direction-01",
          eyebrow: "01",
          title: "기술 가치 제안형 유통",
          body: "이프러스는 고객 목표에 맞는 최적의 솔루션을 제안하고 시스템 설계 지원을 통해 적기 채택을 유도합니다.",
        },
        {
          id: "direction-02",
          eyebrow: "02",
          title: "시장별 특화 유통",
          body: "멀티미디어, 통신, 임베디드 분야를 중심으로 차별화된 서비스를 제공합니다.",
        },
        {
          id: "direction-03",
          eyebrow: "03",
          title: "정확한 수요 예측",
          body: "수요 예측, 납기 준수, 시장 피드백 공유로 고객 프로그램의 안정성을 높입니다.",
        },
        {
          id: "direction-04",
          eyebrow: "04",
          title: "전문 커뮤니케이션",
          body: "고객과 벤더 간 장벽을 낮추고 실무형 피드백을 빠르게 제공합니다.",
        },
      ],
      partnerSignals: [
        "오므니비전",
        "굿릭스",
        "신콤",
        "퀘텍",
        "아이코텍",
        "임베디드 솔루션",
        "기술 지원",
        "시장 피드백",
      ],
      operatingSignals: [
        {
          id: "signal-01",
          title: "시장 이해",
          body: "국제 규범과 국내 고객 니즈를 함께 반영해 제품 적합도와 타이밍을 최적화합니다.",
        },
        {
          id: "signal-02",
          title: "전문적인 인터페이스",
          body: "소싱과 설계 이슈를 분리하지 않고 고객·벤더 간 명확한 커뮤니케이션으로 연결합니다.",
        },
        {
          id: "signal-03",
          title: "지속 협업",
          body: "단발성 거래보다 장기 관계를 위한 자원 운영을 우선합니다.",
        },
      ],
      applicationCards: [
        {
          id: "application-01",
          code: "A1",
          title: "자동차",
          body: "카메라, ADAS, 커넥티비티, 차량 인텔리전스 플랫폼의 안정적 임베디드 통합을 지원합니다.",
        },
        {
          id: "application-02",
          code: "A2",
          title: "텔레매틱스",
          body: "차량·자산 추적과 장치 지능형 통신에 필요한 무선 연결을 지원합니다.",
        },
        {
          id: "application-03",
          code: "A3",
          title: "모바일",
          body: "소형 고성능 연결형 제품에 적합한 고효율 부품과 임베디드 모듈을 제공합니다.",
        },
        {
          id: "application-04",
          code: "A4",
          title: "의료",
          body: "신호 안정성과 응답속도가 중요한 의료 전자기기 통합을 신뢰성 있게 지원합니다.",
        },
        {
          id: "application-05",
          code: "A5",
          title: "보안",
          body: "감시, 출입통제, 안전 시스템에 맞는 비전·센서·통신 부품 구성을 제공합니다.",
        },
        {
          id: "application-06",
          code: "A6",
          title: "멀티미디어",
          body: "디지털 가전 및 멀티미디어 시스템 환경을 위한 부품 소싱 및 임베디드 지원.",
        },
      ],
      workflowSteps: [
        {
          id: "step-01",
          step: "01",
          title: "프로그램 이해",
          body: "소싱 전에 요구사항, 적용 맥락, 일정이 먼저 정렬됩니다.",
        },
        {
          id: "step-02",
          step: "02",
          title: "적합한 벤더/솔루션 매칭",
          body: "목표 사용 케이스에 맞는 벤더, 제품군, 임베디드 방향을 매칭합니다.",
        },
        {
          id: "step-03",
          step: "03",
          title: "설계·수요 지원",
          body: "기술 질의, 수요 예측, 시장 피드백을 하나의 흐름으로 관리합니다.",
        },
        {
          id: "step-04",
          step: "04",
          title: "지속 가능한 납기",
          body: "납기, 커뮤니케이션 품질, 관계 유지가 마지막까지 이어져야 완성됩니다.",
        },
      ],
      highlights: [
        {
          id: "highlight-01",
          title: "경험 기반 팀",
          body: "영업과 엔지니어링 역량을 실행력으로 연결한 팀 구성입니다.",
        },
        {
          id: "highlight-02",
          title: "예측/납기 운영력",
          body: "고객 프로그램에서는 제품 선택만큼 납기 관리와 일정 조율이 중요합니다.",
        },
        {
          id: "highlight-03",
          title: "임베디드 시장 관점",
          body: "임베디드·통신 산업 특성에 맞는 솔루션 파트너 이미지를 반영했습니다.",
        },
      ],
      contactCards: [
        {
          id: "contact-01",
          label: "오피스",
          value: "경기도 성남시 분당구 판교로 255번길 9-22 우림 W-City 708호",
        },
        {
          id: "contact-02",
          label: "전화",
          value: "031-628-8600",
        },
        {
          id: "contact-03",
          label: "팩스",
          value: "031-628-8601",
        },
        {
          id: "contact-04",
          label: "이메일",
          value: "support@eplus.co.kr",
        },
      ],
      actionButtons: {
        joinTeam: "채용 안내",
        about: "회사 소개",
        technologies: "기술 페이지",
        contact: "문의하기",
      },
      footer: {
        brandDesc:
          "1997년부터 임베디드 솔루션, 기술 가이드, 시장 인사이트를 제공해 온 반도체 유통 파트너.",
        companyLinksTitle: "회사",
        supportLinksTitle: "지원",
        officeTitle: "본사",
        aboutEplus: "회사 소개",
        technologies: "기술 영역",
        vendorProducts: "협력사 제품",
        careers: "채용",
        contactUs: "문의하기",
        supportPhone: "031-628-8600",
        supportEmail: "support@eplus.co.kr",
        fax: "팩스: 031-628-8601",
        address: "경기도 성남시 분당구 판교로 255번길 9-22 우림 W-City 708호",
        copyright: "Copyright © 1997 EPLUS CO., LTD. All rights reserved.",
        tagline: "반도체 유통 + 임베디드 솔루션",
      },
    },
  },
};

export const siteCopy = {
  en: {
    footer: {
      desktopLine1:
        "Suite 708, Woolim W-City, 9-22, 255 Pangyo road, Bundang-gu, Sungnam-si, Gyeonggi-do, Korea 13486",
      desktopLine2: "Tel : +82-31-628-8600",
      desktopLine3: "Fax : +82-31-628-8601",
      desktopLine4: "© 1997 EPLUS CO., LTD. ALL RIGHTS RESERVED",
      line1:
        "Suite 708, Woolim W-City, 9-22, 255 Pangyo road, Bundang-gu, Sungnam-si, Gyeonggi-do, Korea 13486",
      line2: "Tel : +82-31-628-8600",
      line3: "Fax : +82-31-628-8601",
      line4: "© 1997 EPLUS CO., LTD. ALL RIGHTS RESERVED",
      mediaAddress:
        "#708, Woolim W-City, 9-22, 255 Pangyo road, Bundang-gu, Sungnam-si, Gyeonggi-do, Korea 13486",
      mediaTel: "+82-31-628-8600",
      mediaFax: "+82-31-628-8601",
      mediaCopy: "© 1997 EPLUS CO., LTD. ALL RIGHTS RESERVED",
    },
    pages: {
      company: {
        title: "Company",
        tabs: ["About", "History"],
        about: {
          headerTitle: "About Eplus",
          contentTitle: "Business Direction",
          whyTitle: "Why eplus?",
          leadParagraph:
            "Eplus is a semiconductor distribution partner focused on practical execution across automotive, telematics, mobile, security, medical, and multimedia markets.",
          bodyParagraphs: [
            "We connect sourcing, design support, forecasting, and communication so customers can build products with fewer delays and fewer blind spots.",
            "Our team provides timely technical guidance, market intelligence, and coordinated vendor support to deliver end-to-end outcomes from planning to shipment.",
          ],
          cards: [
            {
              title: "Supply Technology Value-Added Solutions",
              content1:
                "· Working together with customers for optimal solutions",
              content2: "· Providing technical guidance to system designers",
              content3:
                "· Introducing advanced products and services at the right time",
            },
            {
              title: "Offer Specific Distribution for Particular Marketplaces",
              content1:
                "· Specializing in multimedia, communication, and embedded fields",
              content2:
                "· Delivering differentiated customer information and service",
              content3: "· Guiding vendors based on market and customer needs",
            },
            {
              title: "Provide Accurate Forecasts",
              content1: "· Tracking forecasts for PO’s in a timely manner",
              content2:
                "· Maximizing customer service through on-time delivery",
              content3:
                "· Reporting comprehensive market feedback continuously",
            },
            {
              title: "Establish Professional Communications",
              content1:
                "· Improving accessibility between customers and vendors",
              content2: "· Responding to all inquiries within 24 hours",
              content3: "· Exchanging professional feedbacks regularly",
            },
          ],
          tableRows: [
            [
              "· Well-experienced and motivated sales & engineering team",
              "· Founding members have over 70 years of experience in semiconductor and embedded solution business",
            ],
            [
              "· Strong understanding of international business practices and cultures",
              "· Proper interfaces and communication between customers and vendors",
            ],
            [
              "· Willing to invest resources for long-term partnerships",
              "· Information sharing and market trend feedback for roadmaps",
            ],
            [
              "· Efficient and skilled design work",
              "· Deep relationship with many customers in multimedia and telecom industries",
            ],
          ],
        },
      },
      vendor: {
        title: "Vendor & Product",
        tabs: ["Omnivision", "Goodix", "Syncomm", "Quectel", "Ikotek"],
        omnivision: {
          title: "OmniVision",
          website: "https://www.ovt.com",
          headerIntro: [
            "OmniVision has been leading advanced digital imaging, analog, and display solutions for a broad range of applications since its founding in 1995.",
            "It continues to drive innovation while building partnerships in mobile, automotive, security, computing, smartphone, and emerging technology sectors.",
          ],
          bodyParagraphs: [
            "As a fabless semiconductor company with repeated award-winning innovation, OmniVision works to deliver smoother human-machine interface solutions across automotive, medical, security and surveillance, computing, smartphone, and emerging technology fields.",
            "OmniVision developed the first smartphone camera module in 2002, introduced one of the world’s smallest NTS camera modules in 2006, and launched PureCel™ CMOS image sensors in 2013 to deliver trusted and innovative imaging and sensing performance.",
            "This continuous progress reflects our commitment to demonstrate improvement and commitment to superior quality and service with customer satisfaction as the highest priority.",
          ],
          sections: [
            {
              title: "Mobile Device",
              subtitle:
                "Advanced digital imaging solutions enable smartphone suppliers to deliver professional-grade cameras into the hands of consumers.",
              cards: [
                {
                  title: "High-Performance Camera",
                  content: [
                    "OmniVision continuously advances fundamental image-sensor technology, improving image quality every generation",
                    "to provide better user experiences and more faithful scene reproduction even as pixels become smaller.",
                  ],
                },
                {
                  title: "Dual-Camera Sensor Solutions",
                  content: [
                    "OmniVision offers sensors that support optical zoom and low-light enhancement for dual-camera designs.",
                    "Its sensors include support features such as on-chip EIS and synchronization for robust dual-camera performance.",
                  ],
                },
              ],
            },
            {
              title: "Automotive Imaging",
              subtitle:
                "OmniVision CMOS sensors are designed from the start with automotive needs in mind, balancing image quality, reliability, and cost.",
              cards: [
                {
                  title: "Display-Based Image Systems",
                  content: [
                    "Display-based systems such as rear-view and surround-view cameras and electronic mirrors require exceptional image quality.",
                    "By combining BSI technology with companion ASICs, OmniVision enables compact and cost-effective systems.",
                  ],
                },
                {
                  title: "Surround-View Video",
                  content: [
                    "360-degree surround-view video uses cameras on each side of the vehicle controlled by a central unit",
                    "to generate an overhead stitched view for safer perception and driver confidence.",
                    "OmniVision also provides image sensors and ISPs that process high-quality scenes naturally.",
                  ],
                },
              ],
            },
            {
              title: "Surveillance",
              subtitle:
                "OmniVision provides advanced imaging solutions for enterprise and smart-home surveillance.",
              cards: [
                {
                  title: "Commercial Surveillance System",
                  content: [
                    "Commercial systems demand high-resolution compression to capture and transmit clean 4K video.",
                    "OmniVision portfolio sensors and processing chips are optimized for video analytics and enterprise deployment.",
                  ],
                },
                {
                  title: "Smart-Home Monitoring System",
                  content: [
                    "Battery-operated wireless smart-home cameras benefit from OmniVision’s low-power, high-resolution sensors.",
                    "These sensors also support features such as HDR, fast frame rates, facial recognition, and RGB-IR.",
                  ],
                },
              ],
            },
          ],
          tableHeaders: [
            "Category",
            "Description",
            "Specification",
            "Model",
            "PDF",
          ],
        },
        goodix: {
          title: "Goodix",
          website: "http://www.goodix.com",
          headerIntro: [
            "Founded in 2002, Goodix has become a global leader in Human Interface and Biometric Technologies.",
            "It provides top performance for mobile phones, tablets, laptops, and wearable devices through its touch interface and fingerprint solutions.",
          ],
          bodyParagraphs: [
            "With extensive experience, Goodix delivers best-in-class performance to world-class brands and partners.",
            "In touch and fingerprint solutions, it continuously increases product value through innovative performance.",
          ],
          sections: {
            fingerprintHeading: "Fingerprint Solution",
            touchHeading: "Touch Controllers",
          },
          fingerprintIntro: [
            "Goodix is a leading fingerprint-solution supplier and places security at the center of its design.",
            "It offers a broad range of solutions including Ultrasonic, Optical IN-DISPLAY, and Capacitive Fingerprint sensors.",
          ],
          touchIntro: [
            "Goodix is a high-performance Human Interface solutions provider and aims to deliver a true touch experience.",
            "Applied in billions of devices worldwide, its technologies continue to evolve alongside display and smart mobile development.",
          ],
          tableBoxes: [
            {
              title: "Ultrasonic Fingerprint Sensor",
              text: [
                "Provides secure fingerprint authentication by recognizing 3D fingerprint information.",
              ],
            },
            {
              title: "Optical IN-DISPLAY Fingerprint Sensor",
              text: [
                "An Optical IN-DISPLAY sensor used across many fields, suitable for modern bezel-less device designs.",
              ],
            },
            {
              title: "Capacitive Fingerprint Sensor",
              text: [
                "Provides broad compatibility for smart devices, including side-key and front/back mounted solutions.",
              ],
            },
            {
              title: "Touch Controller for Mobile",
              text: [
                "Supports various LCD and AMOLED display sizes with optimized touch solutions.",
              ],
            },
            {
              title: "Touch Controller for IT (Tablet/Laptops)",
              text: [
                "Supports a wide range of tablet and laptop sizes while providing strong active-stylus performance.",
              ],
            },
            {
              title: "Touch Controller for Automotive",
              text: [
                "Products supporting 8-inch to 17-inch automotive displays adapt flexibly to many aspect ratios.",
              ],
            },
          ],
          touchScreenHeader: [
            "Part Number",
            "Touch Points",
            "Channels",
            "Screen Size",
            "Report Rate",
            "Package",
            "Chip Size",
            "Interface",
          ],
          touchScreenRows: [
            [
              "GT9895",
              "10",
              "17Tx * 34Rx",
              '6.7"',
              "360Hz",
              "BGA72",
              "4.5 × 4.5mm",
              "IIC / I3C / SPI",
            ],
            [
              "GT9916",
              "10",
              "19Tx * 40Rx",
              '7.1"',
              "360Hz",
              "BGA88",
              "5 * 5mm",
              "IIC / I3C / SPI",
            ],
          ],
          touchPedHeader: [
            "Part Number",
            "Channels",
            "Interface",
            "DOME Key",
            "Package",
            "Chip Size",
            "System Support",
            "Supply Voltage",
          ],
          touchPedRows: [
            [
              "GT7863",
              "16Tx * 27Rx",
              "I2C / PS2",
              "Support",
              "QFN60",
              "6 * 6 * 0.6mm",
              "Windows / Chrome",
              "AVDD 2.8 / 3.0 / 3.3V",
            ],
            [
              "GT7868Q",
              "22Tx * 38Rx",
              "I2C",
              "Support",
              "QFN88",
              "10 * 10 * 0.75mm",
              "Windows / Chrome",
              "AVDD 2.8 / 3.0 / 3.3V DVD 1.8V",
            ],
          ],
          activeStylusHeader: [
            "Part Number",
            "Channels",
            "Interface",
            "Support Protocols",
            "Tx Voltage",
            "Supply Voltage",
          ],
          activeStylusRows: [
            [
              "GP850",
              "2Tx * 1Rx",
              "I2C",
              "HPP3.x, MPP2.0, USI1.0, GPP",
              "15 / 20 / 30 / 40V",
              "2.2V",
            ],
          ],
          ultrasonicFingerprintHeader: [
            "Part Number",
            "Status",
            "Applications",
            "Product Type",
            "FOV",
            "Module Size",
            "Supply Voltage",
            "Interface",
          ],
          ultrasonicFingerprintRows: [
            [
              "GUM5228",
              "Active",
              "Under Flexible AMOLED Display",
              "Ultrasonic",
              "5mm * 5mm",
              "16.85mm * 13.4mm * 0.17mm",
              "2.6V ~ 3.6V",
              "SPI",
            ],
          ],
          opticalInDisplayHeader: [
            "Part Number",
            "Status",
            "Applications",
            "Product Type",
            "FOV",
            "Module Size",
            "Supply Voltage",
            "Interface",
          ],
          opticalInDisplayRows: [
            [
              "GW9558",
              "Active",
              "Under AM OLED Display",
              "Optical Lens Type",
              "6.5mm * 6.5mm",
              "7.25mm * 4.8mm * 4.2mm",
              "3.1 ~ 3.6V",
              "SPI",
            ],
            [
              "GW9578",
              "Active",
              "Under AM OLED Display",
              "Optical Lens Type",
              "6.5mm * 6.5mm",
              "5.84mm * 4.63mm * 4.2mm",
              "3.1 ~ 3.6V",
              "SPI",
            ],
          ],
          capacitiveHeader: [
            "Part Number",
            "Status",
            "Applications",
            "Package Size",
            "Supply Voltage",
            "Interface",
          ],
          capacitiveRows: [
            [
              "GF3626",
              "Active",
              "Coating side key",
              "15 * 2.56 * 1.2",
              "2.8V ~ 3.6V",
              "SPI",
            ],
            [
              "GF3956",
              "Active",
              "Coating side key",
              "14.3 * 2.4 * 1.3",
              "2.8V ~ 3.6V",
              "SPI",
            ],
          ],
        },
        syncomm: {
          title: "Syncomm",
          website: "http://www.syncomm.com.tw",
          headerIntro: [
            "Syncomm is a semiconductor design company specializing in real-time wireless digital audio streaming.",
            "Our solutions support high quality audio transmission across portable devices and many platform categories.",
          ],
          bodyParagraphs: [
            "SYNCOMM® is a global leader applying SYNIC® technology, enabling audio quality that can surpass CD-level performance.",
            "SYNIC® technology transmits digital wireless audio using 2.4GHz / 5GHz bands that are not globally pre-assigned.",
            "With an advanced architecture, we provide coexistence support and flexible high-performance wireless transmission.",
          ],
          keyFactorsHeading: "Key Factors",
          keyFactorsText: [
            "In wireless audio streaming, key factors include sound quality, RF interference, power consumption, range, latency, cost, size, and functional completeness.",
            "Customers repeatedly find Syncomm’s solutions provide balanced optimization for their specific applications.",
          ],
          evaluationHeading: "Evaluation Kits",
          evaluationText: [
            "Reference designs and technical support services are available to shorten development time and make prototyping easier.",
            "Our solution platform is used in Dolby headphones, LCD TVs, home theaters, surrounding speakers, home stereo, and many portable applications.",
          ],
          partnersHeading: "Partners",
          partnersText: [
            "To quickly enter the Syncomm market, we provide high-quality service to focused customers and have established a global distribution network.",
            "Our services help customers secure advantages in product development and channel marketing.",
          ],
          featureItems: [
            { title: "LOW Cost", text: ["Low system BOM cost"] },
            {
              title: "Driver Free",
              text: ["No need PC Driver  /  Plug and play"],
            },
            {
              title: "RX Synchronization",
              text: ["Audio phase synchronization", "among RX"],
            },
            { title: "LOW Volatge", text: ["Minimum 2.0V", ""] },
            { title: "2.4GHz", text: ["Global ISM unlicensed band", ""] },
            {
              title: "Small Size",
              text: ["Compact module  /", "Flexible for design-in"],
            },
            { title: "Short Delay Time", text: ["<20ms, programmable", ""] },
            {
              title: "Trukey Solution",
              text: [
                "H/W and S/W ready  /",
                "System design  /  Production kits ready",
              ],
            },
            { title: "5GHz", text: ["Global UNII unlicensed band", ""] },
            {
              title: "Control Back",
              text: ["Bi-directional communication", "/  Full duplext"],
            },
            {
              title: "Coexistence",
              text: ["WiFi  /  Cordless phone  /  Bluetooth", "Microwave"],
            },
            {
              title: "Market Proven Solution",
              text: ["Proven solution  /  Global brands adoption", ""],
            },
            {
              title: "Multiple TX",
              text: ["Up to 8 TX  /", "Transmission synchronization"],
            },
            { title: "Long Distance", text: ["Up to 100 meters", ""] },
            { title: "Low Power Consumption", text: ["<40ma @2.0", ""] },
            { title: "Broadcasting", text: ["Point to multi-points", ""] },
          ],
          featureSummaryTitle: "Summary of Syncomm's 2.4GHz Modules",
          featureSummaryHeader: ["", "IA9QH5", "IA3S4"],
          featureSummaryRows: [
            ["Type", "Tansmitter/Receiver", "Tansmitter/Receiver"],
            ["Power Supply", "2.0V to 3.6V", "2.0V to 3.6V"],
            ["RF Power(Max)", "8dBm", "7dBm"],
            ["Current Consumption (Max)", "60mA", "52mA"],
            ["Sensitivity", "-82dBm", "-80dBm"],
            ["Distance", "15m", ">30m"],
            ["SNR", "98dB", "134dB"],
            ["THD+N", "-98dB", "-59dB"],
            ["Die size", "7mm x 7mm x 0.75mm", "26mm x 60mm x 1.0mm"],
            ["Antenna Type (Fixed)", "PCB", "PCB"],
            ["LDO (Fixed)", "Included", "Not Included"],
            ["Audio Input and Output(Fixed)", "I2S&USB&S/PDIF", "I2S"],
            ["RF Bandwidth", "4~5Ghz", "2.4Ghz ISM"],
          ],
        },
        quectel: {
          title: "Quectel",
          website: "https://www.quectel.com/ko",
          headerIntro: [
            "Since its founding in 2010, Quectel has quickly achieved a leading global market position and continues to lead IoT technology.",
            "As a leading provider of cellular wireless modules, GNSS, and antennas, Quectel connects devices and people to networks and services, helping build a smarter world.",
          ],
          bodyParagraphs: [
            "Quectel’s products make daily life more convenient, efficient, comfortable, richer, and safer.",
            "Driven by passion for a smarter world, we continue to accelerate innovation across IoT solutions and support.",
          ],
          sections: {
            culture: "Quectel's Corporate Culture",
            companyOverview: "Overview of Quectel as a Company",
            history: "History & milestones",
            portfolio: "Quectel IoT portfolio",
            trusted: "Quectel is a trusted partner",
          },
          overviewParagraph:
            "Quectel's passion for a smarter world drives us to accelerate IoT innovation. A highly customer-centric organization, we are a global IoT solutions provider backed by outstanding support and services.",
          portfolioCards: [
            {
              title: "Cellular",
              list: ["5G", "4G", "LPWA", "3G/2G"],
              href: "https://www.quectel.com/product-selector/?_sft_product_supported_technologies=5g",
            },
            {
              title: "Automotive",
              list: [
                "5G/4G",
                "C-V2X",
                "GNSS",
                "Cockpit / IVI",
                "Wi-Fi / Bluetooth",
              ],
              href: "https://www.quectel.com/product-selector/?_sft_product_cat=automotive-modules",
            },
            {
              title: "Smart",
              list: ["5G", "4G", "Edge compute"],
              href: "https://www.quectel.com/product-selector/?_sft_product_cat=smart-modules",
            },
            {
              title: "Wi-Fi/Bluetooth",
              list: [
                "Wi-Fi 7",
                "Wi-Fi 6E",
                "Wi-Fi 6",
                "Wi-Fi 5",
                "Wi-Fi 4",
                "BT 5.X",
                "Sub-G (LoRa/ HaLow)",
              ],
              href: "https://www.quectel.com/product-selector/?_sft_product_cat=wi-fi-bluetooth-modules",
            },
            {
              title: "GNSS",
              list: [
                "DR positioning",
                "RTK positioning",
                "Fusion positioning",
                "Timing",
                "Multiple frequency localization",
                "Single frequency localization",
                "Integrated antenna",
                "IMU",
              ],
              href: "https://www.quectel.com/product-selector/?_sft_product_cat=gnss-modules-standalone",
            },
            {
              title: "Satellite",
              list: ["5G NTN", "Proprietary"],
              href: "https://www.quectel.com/product-selector/?_sft_product_cat=satellite-modules",
            },
            {
              title: "Antenna",
              list: [
                "Embedded antenna",
                "External antenna",
                "Cable & accessories",
                "Automotive antenna",
                "mmWave antenna",
              ],
              href: "https://www.quectel.com/product-selector/?_sft_product_cat=antennas",
            },
            {
              title: "Services",
              list: [
                "Antenna (consultation, design, evaluation and testing)",
                "Certification & testing",
                "RTK correction services",
              ],
              href: "https://www.quectel.com/product-selector/",
            },
          ],
          portfolioFooter:
            "Comprehensive IoT product and services portfolio, enabling a complete solution.",
          trustedDescription:
            "Quectel’s reliability comes from technical depth, long-term global partnerships, and proven partner support.",
          moreText: "More Details +",
        },
        ikotek: {
          title: "Ikotek",
          website: "https://www.ikotek.com/",
          headerIntro: [
            "Ikotek is a trusted IoT ODM (Original Design Manufacturing) partner and a global supplier headquartered in the United States.",
            "We provide one-stop services for IoT device design, manufacturing, and certification.",
          ],
          bodyParagraphs: [
            "We are a one-stop-shop for IoT device design, manufacturing and certification, delivering high-quality products and services to reduce complexity, speed up time-to-market, eliminate risk, and reduce costs.",
            "With proven IoT ODM expertise, we have designed and produced millions of IoT devices for multiple industries.",
            "Our experience enables us to drive innovation and efficiency simultaneously and build a unique value proposition for our customers.",
          ],
          introCards: [
            "A US-based ODM partner with world-wide capabilities",
            "A US-based ODM partner with world-wide capabilities",
            "A US-based ODM partner with world-wide capabilities",
            "A US-based ODM partner with world-wide capabilities",
          ],
          storyHeading: "The Ikotek Story",
          storyParagraph: "Deep roots growing from the IoT industry",
          historyParagraph:
            "The Ikotek ODM team was established in 2019 and now has more than 400 experienced R&D employees, including executives with more than 20 years of average experience in IoT organizations.",
          designHeading: "Flexible Manufacturing and Design Services",
          designParagraph: "Ikotek supports EMS, JDM, and ODM models",
          solutionsHeading: "Ikotek Solutions",
          solutionsParagraph:
            "Our flexible services take you from concept to mass production and cover:",
          currentExamplesHeading: "Current Product Focuses and Examples",
          advantageHeading: "The Ikotek Advantage",
          advantageRows: [
            {
              title: "R&D",
              content:
                "A strong R&D team with rich experience in wireless products, covering 2G/3G/4G/5G/GNSS platforms, with development tools and test equipment leveraging Quectel's ecosystem. Strategy partnership with Qualcomm to provide state-of-the-art products and solutions.",
            },
            {
              title: "Supply Chain",
              content:
                "End-to-end supply chain services with strong global network support to optimize cost and lead time.",
            },
            {
              title: "Production",
              content:
                "ODM products are manufactured in our own facilities and selected CM factories in and outside China.",
            },
            {
              title: "Quality",
              content:
                "A complete quality system with strict hardware, software, and reliability test processes.",
            },
            {
              title: "Global Certification",
              content:
                "Global certification support allows certified designs to reduce certification cost and reduce time-to-market.",
            },
          ],
        },
      },
      technologies: {
        title: "Technologies",
        tabs: [
          "Automotive",
          "Blackbox",
          "Medical",
          "Mobile",
          "Security",
          "Telematics",
          "DEMS",
        ],
        automotive: {
          title: "Automotive",
          introParagraphs: [
            "Vehicle camera was used as a video recorder which can improve the driving pleasure and collect accident evidence.",
            "With the development of technologies like panorama stitching, ADAS, automatic driving, vehicle camera application has been upgraded.",
          ],
          secondParagraphs: [
            "Based on computer vision technology, the camera can collect more information from the video and enable the control system to make decisions.",
            "This leads to better driving experience and improved safety.",
          ],
          highlightsTitle: "Technology Highlight",
          highlights: [
            "Low-Power, High-Quality, FHD, 4K UHD Video",
            "Automotive-level certification and optional front ADAS features available",
            "State-of-the-Art Codec",
            "Multi-channel recorders",
            "High Dynamic Range (HDR)",
            "Intelligent Video Analytics",
          ],
          solutionTitle: "Eplus Solution",
          solution: {
            title: "OmniVision",
            link: "http://www.ovt.com",
            text: [
              "Driver distraction is on the rise, and advanced driver assistance systems (ADAS) are becoming mainstream.",
              "Semi-autonomous and fully autonomous vehicles are not far off on the horizon.",
              "Vision systems are at the heart of ADAS, powered by major technological advancements in CMOS image sensors.",
              "These include camera-enabled systems for automatic emergency braking, autonomous driving, video mirror, rear view, 360-degree surround view and driver monitoring.",
            ],
          },
        },
        blackbox: {
          title: "Blackbox",
          introParagraphs: [
            "Vehicle camera was used as a video recorder which can improve the driving pleasure and collect accident evidence.",
            "With the development of technologies like panorama stitching, ADAS, automatic driving, vehicle camera application has been upgraded.",
          ],
          secondParagraphs: [
            "Based on computer vision technology, the camera can collect more information from the video and enable the control system to make decisions.",
            "This leads to a better experience of driving and improvement of safety.",
          ],
          whatTitle: "What?",
          whatText: [
            "Vehicle black boxes, also called Drive Recorders, are advanced recording systems that store not only videos during driving and parking,",
            "but also vehicle acceleration information at the same time.",
          ],
          whyTitle: "Why?",
          whyText: [
            "As one car per household became common, disputes from automotive accidents are not decreasing.",
            "In many cases of accidents, only one side of the story is often emphasized.",
            "Reliable video records from black boxes help prevent disputed cases.",
          ],
          legalTitle: "Black Box Legal Status",
          imageSensorTitle: "Omnivision Image Sensor",
          tableHeader: [
            "",
            "FULL HD 1920 * 1080",
            "QHD 2688 * 1944",
            "UHD 3840 * 2160",
          ],
          tableRows: [
            [
              "Sensor",
              "OS02H10, OS02C10",
              "OS04C20, OS05B10",
              "OS08G10, OS08C10",
            ],
            [
              "HDR",
              "Staggered HDR / DCG + VS",
              "Staggered HDR",
              "DCG / LOFIC, DAG",
            ],
          ],
          solutionTitle: "Eplus Solution",
          solution: {
            title: "OmniVision",
            link: "http://www.ovt.com",
            text: [
              "Driver distraction is on the rise, and advanced driver assistance systems (ADAS) are becoming mainstream.",
              "Semi-autonomous and fully autonomous vehicles are not far off on the horizon.",
              "Vision systems are at the heart of ADAS, powered by major technological advancements in CMOS image sensors.",
              "These include camera-enabled systems for automatic emergency braking, autonomous driving, video mirror, rear view, 360-degree surround view and driver monitoring.",
            ],
          },
        },
        medical: {
          title: "Medical",
          introParagraphs: [
            "The medical markets for endoscopy imaging solutions are growing rapidly, driven by a need for minimally invasive diagnostic and therapeutic procedures,",
            "socioeconomic trends such as an aging population, and rising medical costs that are pushing procedures back into the physician’s office.",
            "Single-use endoscopes and catheters can have advantages over reusable devices that can have sterilization issues.",
          ],
          threeImageText: [
            "Neurological, ophthalmic, cardiac, spinal, urologic and gynecologic",
            "imaging procedures require small-outer-diameter endoscopes.",
            "Ultra-small CMOS imagers allow chip-on-the-tip designs with higher",
            "resolution, better image quality and lower cost than fiber optics.",
          ],
          solutionTitle: "Eplus Solution",
          solution: {
            title: "OmniVision",
            link: "http://www.ovt.com",
            text: [
              "Driver distraction is on the rise, and advanced driver assistance systems (ADAS) are becoming mainstream.",
              "Semi-autonomous and fully autonomous vehicles are not far off on the horizon.",
              "Vision systems are at the heart of ADAS, powered by major technological advancements in CMOS image sensors.",
              "These include camera-enabled systems for automatic emergency braking, autonomous driving, video mirror, rear view, 360-degree surround view and driver monitoring.",
            ],
          },
        },
        mobile: {
          title: "Mobile",
          introParagraphs: [
            "5G smartphones will be crucial in changing mobile communication norms among users who have access to them.",
            "5G is capable of transmitting data roughly 10 to 100 times faster than 4G, the current standard, and this ability of 5G will support a wide range of IoT-connected devices.",
            "It will also enhance VR and AR experiences and enable faster and more reliable mobile video.",
          ],
          secondParagraph:
            "5G smartphone shipments are expected to reach approximately 600 million units in 2022, accounting for about 31% of the global market.",
          solutionTitle: "Eplus Solution",
          solutions: [
            {
              title: "OmniVision",
              link: "http://www.ovt.com",
              text: [
                "Driver distraction is on the rise, and advanced driver assistance systems (ADAS) are becoming mainstream.",
                "Semi-autonomous and fully autonomous vehicles are not far off on the horizon.",
                "Vision systems are at the heart of ADAS, powered by major technological advancements in CMOS image sensors.",
                "These include camera-enabled systems for automatic emergency braking, autonomous driving, video mirror, rear view, 360-degree surround view and driver monitoring.",
              ],
            },
            {
              title: "Goodix",
              link: "http://www.goodix.com",
              text: [
                "Fresco Logic is the leading developer in providing advanced I/O silicon that deliver highly efficient connectivity such as USB 3.1,",
                "F-One Aggregation, USB Type-C, USB Video, and USB Power Delivery for next-generation consumer electronics, mobile,",
                "and industrial IoT. Today, over one billion USB 3.0/1.0 ports in the world has adopted Fresco Logic technology.",
              ],
            },
            {
              title: "Visualon",
              link: "http://www.visualon.com",
              text: [
                "Fresco Logic is the leading developer in providing advanced I/O silicon that deliver highly efficient connectivity such as USB 3.1,",
                "F-One Aggregation, USB Type-C, USB Video, and USB Power Delivery for next-generation consumer electronics, mobile,",
                "and industrial IoT. Today, over one billion USB 3.0/1.0 ports in the world has adopted Fresco Logic technology.",
              ],
            },
          ],
        },
        security: {
          title: "Security",
          cards: [
            {
              title: "Commercial Surveillance System",
              text: [
                "Commercial surveillance systems require",
                "high-resolution compression technology",
                "to capture and transmit crystal-clear 4K video and",
                "perform video analytics. Sensor developers and",
                "processing chip portfolios are designed with these demands in mind.",
              ],
            },
            {
              title: "Smart-Home Monitoring System",
              text: [
                "Smart-home monitoring systems with battery-operated wireless cameras benefit",
                "from OmniVision’s high-resolution, lower-power and",
                "high-sensitivity image sensors. These sensors also",
                "support advanced features such as HDR, fast frame rates,",
                "facial recognition and RGB-IR technology.",
              ],
            },
            {
              title: "Battery Doorbell Cam",
              text: [
                "Battery-powered doorbell cameras must capture scenes under",
                "varied lighting conditions so that both foreground",
                "and background are visible, eliminating the silhouette effect.",
                "PureCel® image sensors provide high sensitivity and full well capacity",
                "for broader dynamic range.",
              ],
            },
          ],
          solutionTitle: "Eplus Solution",
          solution: {
            title: "OmniVision",
            link: "http://www.ovt.com",
            text: [
              "Driver distraction is on the rise, and advanced driver assistance systems (ADAS) are becoming mainstream.",
              "Semi-autonomous and fully autonomous vehicles are not far off on the horizon.",
              "Vision systems are at the heart of ADAS, powered by major technological advancements in CMOS image sensors.",
              "These include camera-enabled systems for automatic emergency braking, autonomous driving, video mirror, rear view, 360-degree surround view and driver monitoring.",
            ],
          },
        },
        dems: {
          title: "DEMS",
          topTitle: "Driving State Monitoring Alarming System",
          topText: [
            "Drowsiness warning system is an in-vehicle infrared camera-based device that evaluates the driver's eye movement and facial-muscle changes.",
            "When an unsafe condition is detected through blinking rate and face orientation patterns,",
            "it issues warning sounds and strong vibrations to alert the driver.",
          ],
          statsTitle: "Stats Caused by Drowsy Driving",
          statsItems: [
            {
              value: "6%",
              text: "of all vehicle crashes involved a drowsy driver.",
            },
            {
              value: "7%",
              text: "of these crashes led to victims requiring medical care.",
            },
            {
              value: "13%",
              text: "of all crashes required one or more people to be injured.",
            },
            {
              value: "21%",
              text: "of all fatal crashes are believed to involve a drowsy driver.",
            },
            { value: "109,000", text: "of these accidents caused injuries." },
            {
              value: "328,000",
              text: "crashes yearly are the result of drowsy driving.",
            },
            {
              value: "6,400",
              text: "fatal crashes were related to drowsy driving.",
            },
          ],
          statsSource:
            "Source: November 2014 study from the AAA Foundation for Public Safety",
          mountedTitle: "Mounted Photos",
          mountedPhotos: [
            "Above the steering column cover (Recommended)",
            "At the front bottom of the instrument panel",
            "At the front top of the instrument panel",
            "Above the dashboard",
          ],
          partsTitle: "Names/Functions of Each Part",
          parts: [
            {
              label: "Operation status LED:",
              text: [
                "for display of such statuses as the driver's drowsiness,",
                "negligence in keeping eyes forward, and movement of the face beyond the detectable range",
                "(in green / blue / red).",
              ],
            },
            {
              label: "Camera lens:",
              text: [
                "for monitoring of the driver's driving condition in real time.",
              ],
            },
            {
              label: "GPS Status LED:",
              text: [
                "for display of the status of GPS signals.",
                "(only when the optional GPS cable is connected)",
                "- White LED ON being ready to log on with GPS signal",
                "- White LED blinking: waiting for log on, but GPS connected",
                "- White LED OFF: no GPS signal",
              ],
            },
            {
              label: "Infrared LED:",
              text: [
                "for detecting the driver's face at dark nights and in tunnels with the LED lights.",
              ],
            },
            {
              label: "Function switch:",
              text: [
                "for control of the volume and the sensitivity.",
                "- A short press controls volume (Mute / 1~4 step)",
                "- A long press controls sensitivity (High: Level 3, Middle: Level 2, Low: Level 1)",
              ],
            },
            {
              label: "USB Port:",
              text: [
                "for connection of various USB accessories (including LED warning light/vibrator)",
                "and for update of firmware",
              ],
            },
            {
              label: "I/O Port:",
              text: [
                "for interface with an external GPS and other external devices",
                "(car DVRs, navigators, etc.).",
              ],
            },
            {
              label: "Power socket:",
              text: [
                "for connection to the cigarette power cable (DC12 / 24V).",
              ],
            },
          ],
          title01: "Recommended Mounts",
          title02: "Detectable Ranges",
        },
        telematics: {
          title: "Telematics",
          introParagraph: [
            "The latest telematics technology continues to expand these functions.",
            "5G and V2X connectivity make much faster and more stable data exchanges possible.",
            "Through vehicle-to-everything (V2X) communication and smart-city infrastructure, vehicles will dynamically interact",
            "with traffic systems, road networks and other cars, enabling a safer, cleaner and more efficient transport ecosystem.",
            "This is also laying the foundation for semi-autonomous and fully autonomous vehicle operation.",
          ],
          detailParagraphs: [
            [
              "More specifically, drivers can remotely diagnose vehicles via wireless network and",
              "use services for traffic information, life information, driving status, stability and",
              "vehicle management. In emergencies, it can also help trigger quick actions.",
              "Drivers and vehicles are connected organically through the network so that",
              "users can operate and diagnose vehicles remotely and access a broad range of information",
              "inside and outside the car.",
            ],
            [
              "Telematics services are usually formed through collaborations between automotive makers and",
              "telecom operators. In communication technology, telematics is transitioning from 4G LTE",
              "to 5G, and this shift will strongly influence autonomous driving.",
            ],
            [
              "On the vehicle technology side, development has moved from simply improving",
              "high-speed performance toward enhancing user convenience and practicality",
              "through advanced telematics systems.",
            ],
          ],
          solutionTitle: "Eplus Solution",
          solution: {
            title: "QUECTEL",
            link: "https://www.quectel.com",
            text: [
              "Quectel is well-placed to support commercial telematics with a",
              "vast range of durable, compliant, multi-functional modules.",
            ],
          },
        },
      },
      recruit: {
        title: "Recruit",
        tabs: ["Information"],
        info: {
          titlePrefix: "Eplus Member",
          titleSuffix:
            "is continually recruiting talented people to grow together with the company.",
          tag: "Recruitment Guide",
          sectionTitle: "Field and Qualification",
          pcNotice: "For full details, please check the PC version.",
        },
        summary: {
          title: "Recruitment Period & Screening",
          period: "· Recruitment: Open year-round.",
          method: "· Screening: 1st - Document Screening / 2nd - Interview",
          documentsTitle: "Required Documents & Application Method",
          documents: "· Documents: Resume and Cover Letter",
          method2: "· Application: Email Submission ",
        },
      },
      contact: {
        title: "Contact",
        intro: "For any inquiries, feel free to contact us anytime.",
        contactTo: "Contact Eplus",
        items: [
          {
            title: "Office",
            value:
              "Suite 708, Woolim W-City, 9-22 Pangyo-ro 255beon-gil, Bundang-gu, Seongnam-si, Gyeonggi-do",
          },
          { title: "Phone", value: "031-628-8600" },
          { title: "Fax", value: "031-628-8601" },
        ],
      },
    },
  },
  ko: {
    footer: {
      desktopLine1:
        "경기도 성남시 분당구 판교로 255번길 9-22 우림 W-City 708호",
      desktopLine2: "Tel : 031-628-8600",
      desktopLine3: "Fax : 031-628-8601",
      desktopLine4: "저작권 © 1997 EPLUS CO., LTD. 모든 권리 보유",
      line1: "경기도 성남시 분당구 판교로 255번길 9-22 우림 W-City 708호",
      line2: "Tel : 031-628-8600",
      line3: "Fax : 031-628-8601",
      line4: "저작권 © 1997 EPLUS CO., LTD. 모든 권리 보유",
      mediaAddress:
        "경기도 성남시 분당구 판교로 255번길 9-22 우림 W-City 708호",
      mediaTel: "031-628-8600",
      mediaFax: "031-628-8601",
      mediaCopy: "저작권 © 1997 EPLUS CO., LTD. 모든 권리 보유",
    },
    pages: {
      company: {
        title: "회사소개",
        tabs: ["회사소개", "연혁"],
        about: {
          headerTitle: "이프러스 소개",
          contentTitle: "사업 방향",
          whyTitle: "왜 이프러스인가?",
          leadParagraph:
            "(주)이프러스는 1997년 창립 이후 반도체 유통과 임베디드 솔루션 지원 역량을 결합해, 고객의 사업 초기 기획부터 납기 운영까지 함께하는 파트너입니다.",
          bodyParagraphs: [
            "자동차·통신·멀티미디어·보안·의료 등 빠르게 바뀌는 시장에서 고객이 적기에 맞는 부품 선정과 적용을 할 수 있도록 수요 예측, 시장 정보, 공급 전략을 통해 실무형 가이던스를 제공합니다.",
            "글로벌 파트너 네트워크 기반의 신속한 기술지원, 마케팅·로드맵 자문, 정밀한 커뮤니케이션으로 설계·소싱·납기 단계까지 End-to-End로 파트너링합니다.",
          ],
          cards: [
            {
              title: "기술 가치 제안형 유통",
              content1: "· 고객 목표에 맞는 최적의 솔루션 제안",
              content2: "· 시스템 설계에 대한 기술 가이드 제공",
              content3: "· 고성능 신기술 신속 적용",
            },
            {
              title: "시장별 특화 유통",
              content1: "· 멀티미디어·통신 분야 특화",
              content2: "· 차별화된 정보·고객 서비스를 제공",
              content3: "· 시장 및 고객 니즈에 맞춘 벤더 제안",
            },
            {
              title: "정확한 수요 예측",
              content1: "· PO를 기반으로 수요 예측을 제때 추적",
              content2: "· 정확한 납기 및 고객 대응 역량 강화",
              content3: "· 정기적인 시장 조사 결과 공유",
            },
            {
              title: "전문적인 커뮤니케이션",
              content1: "· 고객과 벤더 간 소통 효율 향상",
              content2: "· 24시간 이내 문의 대응",
              content3: "· 정기적인 전문 피드백 교환",
            },
          ],
          tableRows: [
            [
              "· 경험과 열정이 풍부한 영업/엔지니어링 팀",
              "· 창립 멤버는 반도체 및 임베디드 솔루션 분야 70년 이상의 누적 경험을 보유",
            ],
            [
              "· 글로벌 비즈니스 관행과 문화에 대한 깊은 이해",
              "· 고객·벤더 간 적절한 인터페이스와 커뮤니케이션",
            ],
            [
              "· 장기 파트너십을 위한 자원 투자 의지",
              "· 로드맵 수립을 위한 정보 공유 및 시장 동향 피드백",
            ],
            [
              "· 효율적이고 정교한 설계 지원",
              "· 멀티미디어·통신 산업 다수 고객과의 밀접한 관계",
            ],
          ],
        },
      },
      vendor: {
        title: "협력사 및 제품",
        tabs: ["OmniVision", "Goodix", "Syncomm", "Quectel", "Ikotek"],
        omnivision: {
          title: "OmniVision",
          website: "https://www.ovt.com",
          headerIntro: [
            "(주)이프러스에서는 오랜 기간 축적된 파트너십을 바탕으로 OmniVision과 함께 한 글로벌 품질의 디지털 이미징 및 디스플레이 솔루션을 제공하고 있습니다.",
            "(주)이프러스 협력사 기술파트너인 OmniVision은 오랫동안 모바일, 자동차, 보안, 의료, 임베디드 분야의 핵심 고객을 지원해왔습니다.",
          ],
          bodyParagraphs: [
            "OmniVision은 1995년 설립 이후 다양한 산업의 응용을 위한 첨단 디지털 이미징·아날로그·디스플레이 솔루션을 개발 및 공급해 왔습니다.",
            "2002년 최초 휴대폰 카메라 모듈을 개발한 이후, 2006년 세계에서 가장 작은 NTS 카메라 모듈을 선보였고, 2013년 PureCel™ CMOS 이미지 센서를 출시하며 지문처럼 신뢰할 수 있는 혁신적 이미징 기술을 확대해왔습니다.",
            "지속적인 기술 고도화는 저희가 최고의 품질과 서비스를 통해 고객 만족을 최우선으로 실현하고자 하는 의지를 보여줍니다.",
          ],
          sections: [
            {
              title: "모바일 디바이스",
              subtitle:
                "첨단 디지털 이미징은 스마트폰 제조사가 소비자에게 프로페셔널급 카메라 경험을 제공할 수 있도록 지원합니다.",
              cards: [
                {
                  title: "고성능 카메라",
                  content: [
                    "OmniVision은 매 세대별로 이미지 센서의 화질을 개선하여 픽셀이 작아져도 사용자 경험과 장면 재현 품질을 향상시킵니다.",
                    "이에 따라 고해상도 모바일 이미징 성능을 안정적으로 유지합니다.",
                  ],
                },
                {
                  title: "듀얼 카메라 센서 솔루션",
                  content: [
                    "OmniVision은 광학 줌과 저조도 보강이 가능한 듀얼 카메라 설계를 위한 센서군을 보유하고 있습니다.",
                    "온칩 EIS 및 동기화 기능을 통해 양안 동작의 정확성을 확보합니다.",
                  ],
                },
              ],
            },
            {
              title: "자동차 이미지",
              subtitle:
                "자동차 시장 요구를 고려해 설계된 CMOS 센서는 뛰어난 영상 품질과 안정성을 목표로 합니다.",
              cards: [
                {
                  title: "디스플레이 기반 이미지 시스템",
                  content: [
                    "후방 카메라, 서라운드 뷰, 전자미러와 같은 시스템은 탁월한 화질이 필수입니다.",
                    "OmniVision은 BSI 및 ASIC 협업 아키텍처로 비용 효율적인 디스플레이 기반 시스템 구현을 지원합니다.",
                  ],
                },
                {
                  title: "서라운드 뷰 비디오",
                  content: [
                    "차량 4면 카메라 이미지를 중앙 제어장치로 통합하여 상공 시점 영상을 생성하는 360도 시야 기능을 제공합니다.",
                    "더욱 자연스러운 장면 재현을 위해 고품질 이미지 센서와 ISP 기술을 활용합니다.",
                  ],
                },
              ],
            },
            {
              title: "감시 솔루션",
              subtitle:
                "OmniVision은 기업형 및 스마트홈 감시 시장을 위한 고급 이미지 시스템을 제공합니다.",
              cards: [
                {
                  title: "기업형 감시 시스템",
                  content: [
                    "상용 감시 시스템은 고해상도 압축 및 4K 송출 품질이 중요합니다.",
                    "OmniVision의 처리칩과 센서 포트폴리오는 이러한 요구를 만족하는 설계를 제공합니다.",
                  ],
                },
                {
                  title: "스마트홈 모니터링",
                  content: [
                    "배터리 구동 무선 카메라에서는 저전력 고해상도 센서 성능이 중요합니다.",
                    "OmniVision 센서는 HDR, 고속 프레임, 얼굴 인식, RGB-IR과 같은 고급 기능을 지원합니다.",
                  ],
                },
              ],
            },
          ],
          tableHeaders: ["카테고리", "설명", "사양", "모델", "PDF"],
        },
        goodix: {
          title: "Goodix",
          website: "http://www.goodix.com",
          headerIntro: [
            "Goodix는 2002년에 설립되어, 세계적으로 Human Interface와 Biometric Technologies를 선도하는 기업으로 성장하였습니다.",
            "모바일폰, 태블릿, 랩톱, 웨어러블 디바이스의 터치 인터페이스 및 지문 솔루션에서 높은 기술력을 보유하고 있습니다.",
          ],
          bodyParagraphs: [
            "풍부한 경험을 기반으로 글로벌 선도 기업에게 최적의 성능을 제공하고 있습니다.",
            "특히 Touch 및 Fingerprint Solution 분야에서의 혁신으로 고객 가치 향상에 기여합니다.",
          ],
          sections: {
            fingerprintHeading: "Fingerprint Solution",
            touchHeading: "Touch Controllers",
          },
          fingerprintIntro: [
            "Goodix는 보편적으로 사용되는 지문 솔루션 공급업체로서 보안을 최우선으로 생각합니다.",
            "초음파, 광학 IN-DISPLAY, 정전식 지문 센서를 포함한 광범위한 지문 솔루션을 제공합니다.",
          ],
          touchIntro: [
            "Goodix는 고객에게 진정한 터치 경험을 제공하는 Human-interface 솔루션 기업입니다.",
            "전 세계 10억 개 이상 기기에 적용되어 있으며, 디스플레이 기술 및 스마트 디바이스 발전에 맞춰 혁신적 성능을 제공합니다.",
          ],
          tableBoxes: [
            {
              title: "초음파 지문 센서",
              text: ["3D 지문 정보를 인식해 지문 인증 보안 성능을 강화합니다."],
            },
            {
              title: "광학 IN-DISPLAY 지문 센서",
              text: [
                "베젤리스 디자인에 적합한 광학 IN-DISPLAY 지문 센서로 다수 분야에서 활용됩니다.",
              ],
            },
            {
              title: "정전식 지문 센서",
              text: [
                "사이드키 및 전면·후면 장착형 솔루션 등 다양한 스마트 디바이스에 폭넓게 적용됩니다.",
              ],
            },
            {
              title: "모바일용 터치 컨트롤러",
              text: [
                "LCD/AMOLED 등 다양한 디스플레이 크기에 최적화된 터치 솔루션을 제공합니다.",
              ],
            },
            {
              title: "IT용 터치 컨트롤러",
              text: [
                "태블릿 및 랩톱 기기 크기별로 적용 가능한 제품군으로 고성능 액티브 스타일러스 사용성이 강점입니다.",
              ],
            },
            {
              title: "자동차용 터치 컨트롤러",
              text: [
                "8인치~17인치 디스플레이를 지원하며 다양한 비율의 자동차 화면에 유연하게 대응합니다.",
              ],
            },
          ],
          touchScreenHeader: [
            "모델명",
            "터치 포인트",
            "채널",
            "화면 크기",
            "리포트레이트",
            "패키지",
            "칩 크기",
            "인터페이스",
          ],
          touchScreenRows: [
            [
              "GT9895",
              "10",
              "17Tx * 34Rx",
              '6.7"',
              "360Hz",
              "BGA72",
              "4.5 × 4.5mm",
              "IIC / I3C / SPI",
            ],
            [
              "GT9916",
              "10",
              "19Tx * 40Rx",
              '7.1"',
              "360Hz",
              "BGA88",
              "5 * 5mm",
              "IIC / I3C / SPI",
            ],
          ],
          touchPedHeader: [
            "모델명",
            "채널 수",
            "인터페이스",
            "DOME KEY",
            "패키지",
            "칩 크기",
            "지원 OS",
            "동작 전압",
          ],
          touchPedRows: [
            [
              "GT7863",
              "16Tx * 27Rx",
              "I2C / PS2",
              "지원",
              "QFN60",
              "6 * 6 * 0.6mm",
              "Windows / Chrome",
              "AVDD 2.8 / 3.0 / 3.3V",
            ],
            [
              "GT7868Q",
              "22Tx * 38Rx",
              "I2C",
              "지원",
              "QFN88",
              "10 * 10 * 0.75mm",
              "Windows / Chrome",
              "AVDD 2.8 / 3.0 / 3.3V DVD 1.8V",
            ],
          ],
          activeStylusHeader: [
            "모델명",
            "채널 수",
            "인터페이스",
            "지원 프로토콜",
            "Tx 전압",
            "동작 전압",
          ],
          activeStylusRows: [
            [
              "GP850",
              "2Tx * 1Rx",
              "I2C",
              "HPP3.x, MPP2.0, USI1.0, GPP",
              "15 / 20 / 30 / 40V",
              "2.2V",
            ],
          ],
          ultrasonicFingerprintHeader: [
            "모델명",
            "상태",
            "용도",
            "제품 유형",
            "화면 범위",
            "모듈 크기",
            "동작 전압",
            "인터페이스",
          ],
          ultrasonicFingerprintRows: [
            [
              "GUM5228",
              "Active",
              "유연 AMOLED 디스플레이 하단",
              "Ultrasonic",
              "5mm * 5mm",
              "16.85mm * 13.4mm * 0.17mm",
              "2.6V ~ 3.6V",
              "SPI",
            ],
          ],
          opticalInDisplayHeader: [
            "모델명",
            "상태",
            "용도",
            "제품 유형",
            "화면 범위",
            "모듈 크기",
            "동작 전압",
            "인터페이스",
          ],
          opticalInDisplayRows: [
            [
              "GW9558",
              "Active",
              "AM OLED 디스플레이 하단",
              "광학 렌즈형",
              "6.5mm * 6.5mm",
              "7.25mm * 4.8mm * 4.2mm",
              "3.1 ~ 3.6V",
              "SPI",
            ],
            [
              "GW9578",
              "Active",
              "AM OLED 디스플레이 하단",
              "광학 렌즈형",
              "6.5mm * 6.5mm",
              "5.84mm * 4.63mm * 4.2mm",
              "3.1 ~ 3.6V",
              "SPI",
            ],
          ],
          capacitiveHeader: [
            "모델명",
            "상태",
            "용도",
            "패키지 크기",
            "동작 전압",
            "인터페이스",
          ],
          capacitiveRows: [
            [
              "GF3626",
              "Active",
              "코팅 사이드 키",
              "15 * 2.56 * 1.2",
              "2.8V ~ 3.6V",
              "SPI",
            ],
            [
              "GF3956",
              "Active",
              "코팅 사이드 키",
              "14.3 * 2.4 * 1.3",
              "2.8V ~ 3.6V",
              "SPI",
            ],
          ],
        },
        syncomm: {
          title: "Syncomm",
          website: "http://www.syncomm.com.tw",
          headerIntro: [
            "Syncomm은 실시간 무선 디지털 오디오 스트리밍에 특화된 반도체 설계 전문 기업입니다.",
            "휴대형 기기부터 다양한 솔루션군까지 고품질 오디오 전송을 지원합니다.",
          ],
          bodyParagraphs: [
            "SYNCOMM®은 창의적이고 혁신적인 SYNIC® 기술로 CD 품질을 넘어서는 오디오 성능을 제품에 적용할 수 있게 하는 글로벌 선도 업체입니다.",
            "SYNIC® 솔루션은 전 세계적으로 비할당 주파수 대역(2.4GHz / 5GHz)에서 디지털 무선 오디오를 전송합니다.",
            "고도화된 아키텍처 기반으로 뛰어난 공존성능과 유연한 고성능 무선 전송을 제공합니다.",
          ],
          keyFactorsHeading: "KEY FACTORS",
          keyFactorsText: [
            "무선 오디오 스트리밍의 핵심은 음질, RF 간섭, 전력소모량, 전송거리, 지연시간, 비용, 사이즈 및 다양한 기능입니다.",
            "고객사들은 SYNIC® 기술을 통해 이러한 핵심 지표를 특정 응용에 맞춰 최적화할 수 있음을 확인했습니다.",
          ],
          evaluationHeading: "EVALUATION KITS",
          evaluationText: [
            "기술지원과 레퍼런스 디자인을 통해 개발 기간 단축과 손쉬운 개발을 지원합니다.",
            "Dolby 헤드폰, LCD-TV, 홈시어터, 서라운딩 스피커, 홈 스테레오, 휴대형 응용 등 폭넓게 적용됩니다.",
          ],
          partnersHeading: "PARTNERS",
          partnersText: [
            "Syncomm 시장으로의 신속 진입을 위해 핵심 고객 대상 고품질 서비스를 제공하고 글로벌 유통망을 운영합니다.",
            "제품개발 및 채널 마케팅 경쟁력 확보를 위한 지원을 제공합니다.",
          ],
          featureItems: [
            { title: "저가 구조", text: ["낮은 시스템 BOM 비용"] },
            {
              title: "드라이버 없이",
              text: ["PC 드라이버 불필요", "플러그 앤 플레이"],
            },
            { title: "RX 동기화", text: ["RX 간 오디오 위상 동기화"] },
            { title: "저전압", text: ["최소 2.0V"] },
            { title: "2.4GHz", text: ["글로벌 ISM 비면허 대역"] },
            {
              title: "소형 크기",
              text: ["컴팩트 모듈", "디자인인 유연성 확보"],
            },
            { title: "저지연", text: ["<20ms, 프로그래밍 가능"] },
            {
              title: "트루키 솔루션",
              text: ["H/W·S/W 완비", "시스템 설계 및 생산 키트 지원"],
            },
            { title: "5GHz", text: ["글로벌 UNII 비면허 대역"] },
            {
              title: "양방향 제어",
              text: ["양방향 통신 지원", "완전한 Full-duplex"],
            },
            {
              title: "공존",
              text: [
                "WiFi / 무선전화 / Bluetooth",
                "마이크로파 환경 동시 동작",
              ],
            },
            {
              title: "검증된 솔루션",
              text: ["글로벌 브랜드 채택", "검증된 솔루션 신뢰성"],
            },
            { title: "멀티 TX", text: ["최대 8개 TX", "동기화 전송"] },
            { title: "장거리", text: ["최대 100m"] },
            { title: "저전력", text: ["<40mA @ 2.0V"] },
            { title: "브로드캐스트", text: ["멀티 포인트 송신"] },
          ],
          featureSummaryTitle: "Syncomm 2.4GHz 모듈 요약",
          featureSummaryHeader: ["", "IA9QH5", "IA3S4"],
          featureSummaryRows: [
            ["형태", "송수신기", "송수신기"],
            ["전원 공급", "2.0V ~ 3.6V", "2.0V ~ 3.6V"],
            ["최대 RF 출력", "8dBm", "7dBm"],
            ["최대 전류", "60mA", "52mA"],
            ["감도", "-82dBm", "-80dBm"],
            ["거리", "15m", ">30m"],
            ["신호대잡음비", "98dB", "134dB"],
            ["THD+N", "-98dB", "-59dB"],
            ["칩 크기", "7mm x 7mm x 0.75mm", "26mm x 60mm x 1.0mm"],
            ["고정형 안테나", "PCB", "PCB"],
            ["LDO (고정)", "포함", "미포함"],
            ["오디오 입출력(고정)", "I2S&USB&S/PDIF", "I2S"],
            ["RF 대역폭", "4~5Ghz", "2.4Ghz ISM"],
          ],
        },
        quectel: {
          title: "Quectel",
          website: "https://www.quectel.com/ko",
          headerIntro: [
            "Quectel은 2010년 설립 이래 글로벌 시장 점유율 1위를 달성하며 IoT 기술을 선도하고 있습니다.",
            "셀룰러 모듈, GNSS, 안테나 분야의 선도 공급사로서 디바이스와 사용자, 네트워크 및 서비스를 연결해 더 스마트한 세상을 만듭니다.",
          ],
          bodyParagraphs: [
            "Quectel 제품은 생활을 더 편리하고 효율적이며 안전하게 만들기 위해 설계되었습니다.",
            "우리는 스마트 세상에 대한 열정으로 IoT 혁신을 가속화하고, 글로벌 고객 지원 체계를 기반으로 서비스를 제공합니다.",
          ],
          sections: {
            culture: "Quectel 기업 문화",
            companyOverview: "회사 개요",
            history: "연혁과 마일스톤",
            portfolio: "IoT 포트폴리오",
            trusted: "신뢰받는 파트너",
          },
          overviewParagraph:
            "Quectel은 스마트한 세상에 대한 열정으로 IoT 혁신을 가속하며, 우수한 지원과 서비스 역량을 갖춘 글로벌 IoT 솔루션 파트너입니다.",
          portfolioCards: [
            {
              title: "Cellular",
              list: ["5G", "4G", "LPWA", "3G/2G"],
              href: "https://www.quectel.com/product-selector/?_sft_product_supported_technologies=5g",
            },
            {
              title: "Automotive",
              list: [
                "5G/4G",
                "C-V2X",
                "GNSS",
                "Cockpit / IVI",
                "Wi-Fi / Bluetooth",
              ],
              href: "https://www.quectel.com/product-selector/?_sft_product_cat=automotive-modules",
            },
            {
              title: "Smart",
              list: ["5G", "4G", "Edge compute"],
              href: "https://www.quectel.com/product-selector/?_sft_product_cat=smart-modules",
            },
            {
              title: "Wi-Fi/Bluetooth",
              list: [
                "Wi-Fi 7",
                "Wi-Fi 6E",
                "Wi-Fi 6",
                "Wi-Fi 5",
                "Wi-Fi 4",
                "BT 5.X",
                "Sub-G (LoRa/ HaLow)",
              ],
              href: "https://www.quectel.com/product-selector/?_sft_product_cat=wi-fi-bluetooth-modules",
            },
            {
              title: "GNSS",
              list: [
                "DR",
                "RTK",
                "퓨전",
                "Timing",
                "멀티 주파수",
                "단일 주파수",
                "통합 안테나",
                "IMU",
              ],
              href: "https://www.quectel.com/product-selector/?_sft_product_cat=gnss-modules-standalone",
            },
            {
              title: "Satellite",
              list: ["5G NTN", "독자형 솔루션"],
              href: "https://www.quectel.com/product-selector/?_sft_product_cat=satellite-modules",
            },
            {
              title: "Antenna",
              list: [
                "임베디드 안테나",
                "외장형 안테나",
                "케이블 및 액세서리",
                "자동차용 안테나",
                "mmWave 안테나",
              ],
              href: "https://www.quectel.com/product-selector/?_sft_product_cat=antennas",
            },
            {
              title: "Services",
              list: [
                "안테나(컨설팅, 설계, 평가, 테스트)",
                "인증 및 시험",
                "RTK 보정 서비스",
              ],
              href: "https://www.quectel.com/product-selector/",
            },
          ],
          portfolioFooter:
            "완성형 IoT 제품군과 서비스를 통한 완전한 솔루션 제안",
          trustedDescription:
            "기술력, 장기 글로벌 파트너십, 검증된 협력 지원으로 신뢰를 지속적으로 확보합니다.",
          moreText: "자세히 보기 +",
        },
        ikotek: {
          title: "Ikotek",
          website: "https://www.ikotek.com/",
          headerIntro: [
            "Ikotek은 IoT ODM(Original Design Manufacturing) 전문 파트너로, 미국 본사 기반의 글로벌 공급사입니다.",
            "디바이스 설계·제조·인증 전 과정을 원스톱으로 제공해 개발 복잡도와 리스크를 낮추고, 시장 진입 속도를 높입니다.",
          ],
          bodyParagraphs: [
            "우리는 IoT 기기 설계, 제조, 인증을 위한 원스톱 서비스를 제공하여 높은 품질의 제품과 서비스를 통해 복잡한 개발 과정을 단순화합니다.",
            "검증된 IoT ODM 역량으로 여러 산업군에서 수백만 대 이상의 IoT 기기를 설계·생산했습니다.",
            "풍부한 경험을 바탕으로 혁신과 효율을 동시에 높이며, 고객에게 차별화된 사업 가치를 제공합니다.",
          ],
          introCards: [
            "전 세계 역량을 보유한 미국 기반 ODM 파트너",
            "전 세계 역량을 보유한 미국 기반 ODM 파트너",
            "전 세계 역량을 보유한 미국 기반 ODM 파트너",
            "전 세계 역량을 보유한 미국 기반 ODM 파트너",
          ],
          storyHeading: "Ikotek 스토리",
          storyParagraph: "IoT 산업 성장의 깊은 기반",
          historyParagraph:
            "Ikotek ODM 조직은 2019년 출범했으며, 현재 400명 이상의 연구개발 인력과 평균 20년 이상의 IoT 실무 경력을 가진 경영진으로 구성되어 있습니다.",
          designHeading: "유연한 제조 및 설계 서비스",
          designParagraph: "Ikotek은 EMS/JDM/ODM 모델을 모두 지원합니다.",
          solutionsHeading: "Ikotek 솔루션",
          solutionsParagraph:
            "Ikotek은 컨셉 단계부터 양산까지 다음 서비스를 제공합니다.",
          currentExamplesHeading: "현재 제품 포커스 및 적용 사례",
          advantageHeading: "Ikotek 장점",
          advantageRows: [
            {
              title: "R&D",
              content:
                "무선 제품(2G/3G/4G/5G/GNSS) 경험이 풍부한 R&D 조직으로, Quectel의 개발 장비 및 테스트 장비를 활용합니다. Qualcomm과의 전략적 파트너십으로 최신 제품/솔루션을 제공합니다.",
            },
            {
              title: "공급망",
              content:
                "강력한 글로벌 공급망을 활용해 원가와 리드타임을 최적화하는 엔드투엔드 공급망 서비스를 제공합니다.",
            },
            {
              title: "생산",
              content:
                "중국 내외 생산공장을 활용하여 ODM 제품을 자체 공장 및 협력 CM 공장에서 제조합니다.",
            },
            {
              title: "품질",
              content:
                "하드웨어·소프트웨어·신뢰성 시험을 포함한 완성형 품질 체계와 엄격한 테스트 프로세스로 관리합니다.",
            },
            {
              title: "글로벌 인증",
              content:
                "글로벌 인증 지원을 통해 인증 비용과 인증 소요 시간을 절감하는 설계를 제공합니다.",
            },
          ],
        },
      },
      technologies: {
        title: "기술 영역",
        tabs: [
          "자동차",
          "블랙박스",
          "의료",
          "모바일",
          "보안",
          "텔레매틱스",
          "DEMS",
        ],
      },
      recruit: {
        title: "채용",
        tabs: ["채용안내"],
        info: {
          titlePrefix: "Eplus 구성원",
          titleSuffix:
            "은 함께 성장하는 가치를 만들어 갈 우수 인재를 항상 모집하고 있습니다.",
          tag: "인력 채용 모집요강",
          sectionTitle: "모집부문 및 자격",
          pcNotice: "상세 내용은 PC 버전에서 확인 가능합니다.",
        },
        summary: {
          title: "모집시기 및 전형방법",
          period: "· 모집시기 : 연중 상시 모집합니다.",
          method: "· 전형방법 : 1차 - 서류전형 / 2차 - 면접",
          documentsTitle: "제출서류 및 지원방법",
          documents: "· 제출서류 : 이력서, 자기소개서",
          method2: "· 지원방법 : e-mail 접수 ",
        },
      },
      contact: {
        title: "문의",
        intro: "궁금한 사항이 있다면 언제든지 알려주세요.",
        contactTo: "문의처",
        items: [
          {
            title: "주소",
            value: "경기도 성남시 분당구 판교로 255번길 9-22 우림 W-City 708호",
          },
          { title: "전화", value: "031-628-8600" },
          { title: "팩스", value: "031-628-8601" },
        ],
      },
      technologies: {
        title: "기술 영역",
        tabs: [
          "자동차",
          "블랙박스",
          "의료",
          "모바일",
          "보안",
          "텔레매틱스",
          "DEMS",
        ],
        automotive: {
          title: "자동차",
          introParagraphs: [
            "차량 카메라는 운전의 편의성을 높이고 사고 증거를 확보할 수 있는 영상 기록 장치로 활용됩니다.",
            "파노라마 스티칭, ADAS, 자율주행 기술이 발달하면서 차량 카메라 활용은 한층 고도화되었습니다.",
          ],
          secondParagraphs: [
            "컴퓨터 비전 기술을 기반으로 카메라는 영상에서 더 많은 정보를 수집하여",
            "제어 시스템이 정확히 판단할 수 있게 합니다.",
            "이는 더 나은 주행 경험과 안전성 향상으로 이어집니다.",
          ],
          highlightsTitle: "기술 하이라이트",
          highlights: [
            "저전력, 고화질 FHD, 4K UHD 영상",
            "자동차급 인증 및 전면 ADAS 옵션 지원",
            "최신 코덱 적용",
            "멀티 채널 레코더",
            "HDR (High Dynamic Range)",
            "지능형 비디오 분석",
          ],
          solutionTitle: "Eplus 솔루션",
          solution: {
            title: "OmniVision",
            link: "http://www.ovt.com",
            text: [
              "운전자 주의 분산이 증가함에 따라 고도화된 ADAS가 본격화되고 있습니다.",
              "반자율 및 완전 자율주행 차량은 더 이상 먼 미래가 아닙니다.",
              "CMOS 이미지 센서의 핵심 기술 발전은 ADAS의 시야 처리 성능을 좌우하고 있습니다.",
              "자동 긴급 제동, 자율주행, 차량 후면 카메라, 360도 서라운드 뷰, 운전자 모니터링을 위한 센서 솔루션을 제공합니다.",
            ],
          },
        },
        blackbox: {
          title: "블랙박스",
          introParagraphs: [
            "차량 카메라는 운전의 편의성을 높이고 사고 증거를 확보할 수 있는 영상 기록 장치로 활용됩니다.",
            "파노라마 스티칭, ADAS, 자율주행 기술이 발달하면서 차량 카메라 활용은 한층 고도화되었습니다.",
          ],
          secondParagraphs: [
            "컴퓨터 비전 기술을 기반으로 카메라는 영상에서 더 많은 정보를 수집하여",
            "제어 시스템이 정확히 판단할 수 있게 합니다.",
            "이는 더 나은 주행 경험과 안전성 향상으로 이어집니다.",
          ],
          whatTitle: "What?",
          whatText: [
            "차량용 블랙박스는 Drive Recorder라고도 불리며,",
            "주행 중이거나 주차 중 발생하는 모든 상황의 영상을",
            "기록·저장하고 차량의 가속도 정보도 함께 저장하는 고도화된 영상장치입니다.",
          ],
          whyTitle: "Why?",
          whyText: [
            "가구당 자동차 1대 보유가 보편화되면서",
            "교통사고 분쟁이 끊이지 않습니다.",
            "블랙박스는 한쪽 주장에 의존하지 않고 사고 이력을 객관적으로 확보하는 데 필요합니다.",
          ],
          legalTitle: "블랙박스 사용의 법제화",
          imageSensorTitle: "Omnivision 이미지센서",
          tableHeader: [
            "",
            "FULL HD 1920 * 1080",
            "QHD 2688 * 1944",
            "UHD 3840 * 2160",
          ],
          tableRows: [
            [
              "Sensor",
              "OS02H10, OS02C10",
              "OS04C20, OS05B10",
              "OS08G10, OS08C10",
            ],
            [
              "HDR",
              "Staggered HDR / DCG + VS",
              "Staggered HDR",
              "DCG / LOFIC, DAG",
            ],
          ],
          solutionTitle: "Eplus 솔루션",
          solution: {
            title: "OmniVision",
            link: "http://www.ovt.com",
            text: [
              "운전자 주의 분산이 증가함에 따라 고도화된 ADAS가 본격화되고 있습니다.",
              "반자율 및 완전 자율주행 차량은 더 이상 먼 미래가 아닙니다.",
              "CMOS 이미지 센서의 핵심 기술 발전은 ADAS의 시야 처리 성능을 좌우합니다.",
              "자동 긴급 제동, 자율주행, 차선/후방/후시야 시스템, 운전자 모니터링에 대응합니다.",
            ],
          },
        },
        medical: {
          title: "의료",
          introParagraphs: [
            "내시경 영상 시장은 미세침습 진단·치료 수요 증가로 빠르게 성장하고 있습니다.",
            "고령화, 의료비 상승으로 인해 내시경 시술이 병원 외래 환경에서 더 중요해지고 있습니다.",
            "일회용 내시경/카테터는 멸균 이슈가 있는 반복 사용 기기에 비해 장점이 있습니다.",
          ],
          threeImageText: [
            "신경계, 안과, 심장, 척추, 비뇨기, 산부인과 영상 시술에는",
            "작고 얇은 내경의 내시경이 필요합니다.",
            "초소형 CMOS 이미지 모듈은 칩-온-티팁 구조를 가능하게 하여",
            "광섬유 대비 더 높은 해상도, 더 나은 영상 품질, 더 낮은 비용을 제공합니다.",
          ],
          solutionTitle: "Eplus 솔루션",
          solution: {
            title: "OmniVision",
            link: "http://www.ovt.com",
            text: [
              "운전자 주의 분산이 증가함에 따라 고도화된 ADAS가 본격화되고 있습니다.",
              "반자율 및 완전 자율주행 차량은 더 이상 먼 미래가 아닙니다.",
              "CMOS 이미지 센서의 핵심 기술 발전은 ADAS의 시야 처리 성능을 좌우합니다.",
              "자동 긴급 제동, 자율주행, 차선/후방/후시야 시스템, 운전자 모니터링에 대응합니다.",
            ],
          },
        },
        mobile: {
          title: "모바일",
          introParagraphs: [
            "5G 스마트폰은 접속 가능한 이용자에게 모바일 통신의 기준을 바꾸는 핵심 기기입니다.",
            "5G는 기존 4G 대비 약 10~100배 빠른 데이터 전송이 가능해 IoT 기기 확장을 가속화합니다.",
            "또한 VR/AR 체감도 개선과 더 안정적인 영상 통신을 가능하게 합니다.",
          ],
          secondParagraph:
            "5G 스마트폰 출하량은 2022년에 약 6억 대, 전 세계 시장의 약 31%를 차지할 것으로 예상됩니다.",
          solutionTitle: "Eplus 솔루션",
          solutions: [
            {
              title: "OmniVision",
              link: "http://www.ovt.com",
              text: [
                "운전자 주의 분산이 증가함에 따라 고도화된 ADAS가 본격화되고 있습니다.",
                "반자율 및 완전 자율주행 차량은 더 이상 먼 미래가 아닙니다.",
                "CMOS 이미지 센서의 핵심 기술 발전은 ADAS의 시야 처리 성능을 좌우합니다.",
                "자동 긴급 제동, 자율주행, 차선/후방/후시야 시스템, 운전자 모니터링에 대응합니다.",
              ],
            },
            {
              title: "Goodix",
              link: "http://www.goodix.com",
              text: [
                "Fresco Logic는 USB 3.1, F-One Aggregation, USB Type-C, USB Video, USB Power Delivery 등",
                "차세대 소비자 전자기기·모바일·산업 IoT를 위한 효율 높은 연결을 제공하는",
                "고급 I/O 실리콘을 선도적으로 개발하고 있습니다.",
                "전 세계 10억 개 이상의 USB 3.0/1.0 포트가 Fresco Logic 기술을 채택하고 있습니다.",
              ],
            },
            {
              title: "Visualon",
              link: "http://www.visualon.com",
              text: [
                "Fresco Logic는 USB 3.1, F-One Aggregation, USB Type-C, USB Video, USB Power Delivery 등",
                "차세대 소비자 전자기기·모바일·산업 IoT를 위한 효율 높은 연결을 제공하는",
                "고급 I/O 실리콘을 선도적으로 개발하고 있습니다.",
                "전 세계 10억 개 이상의 USB 3.0/1.0 포트가 Fresco Logic 기술을 채택하고 있습니다.",
              ],
            },
          ],
        },
        security: {
          title: "보안",
          cards: [
            {
              title: "상용 감시 시스템",
              text: [
                "상용 감시 시스템은 선명한 4K 영상을 수집·전송하기 위해",
                "고해상도 압축 기술이 필요합니다.",
                "OmniVision은 영상 분석을 고려한 고해상도 센서/처리칩 포트폴리오를 제공합니다.",
              ],
            },
            {
              title: "스마트홈 모니터링",
              text: [
                "스마트홈용 무선 카메라는 배터리 기반 사용이 많아",
                "저전력, 고감도, 고해상도 센서의 효율이 중요합니다.",
                "HDR, 초고속 프레임, 얼굴 인식, RGB-IR 지원이 가능하도록",
                "고성능 이미지 센서가 적용됩니다.",
              ],
            },
            {
              title: "배터리 구동 도어벨 카메라",
              text: [
                "배터리 구동형 도어벨 카메라는 낮과 밤 모두에서",
                "전경과 배경이 모두 잘 보이도록 동작해야 합니다.",
                "PureCel® 센서는 실루엣 현상을 줄이고 고감도, 넓은 동적 범위를 제공합니다.",
              ],
            },
          ],
          solutionTitle: "Eplus 솔루션",
          solution: {
            title: "OmniVision",
            link: "http://www.ovt.com",
            text: [
              "운전자 주의 분산이 증가함에 따라 고도화된 ADAS가 본격화되고 있습니다.",
              "반자율 및 완전 자율주행 차량은 더 이상 먼 미래가 아닙니다.",
              "CMOS 이미지 센서의 핵심 기술 발전은 ADAS의 시야 처리 성능을 좌우합니다.",
              "자동 긴급 제동, 자율주행, 차선/후방/후시야 시스템, 운전자 모니터링에 대응합니다.",
            ],
          },
        },
        dems: {
          title: "DEMS",
          topTitle: "운전 상태 모니터링 알람 시스템",
          topText: [
            "졸음운전 경보 시스템은 차 내부에 장착한 적외선 카메라로",
            "운전자의 눈동자와 안면 근육 변화를 분석해 상태를 판단합니다.",
            "비정상 상태로 판단되면 경고음과 진동으로 운전자에게 즉시 알림을 제공합니다.",
          ],
          statsTitle: "졸음운전 관련 통계",
          statsItems: [
            { value: "6%", text: "의 차량사고가 졸음운전으로 발생했습니다." },
            {
              value: "7%",
              text: "이 사고 중 일부는 치료가 필요한 부상을 동반했습니다.",
            },
            {
              value: "13%",
              text: "의 사고는 한 명 이상이 아닌 여러 명이 다치게 되었습니다.",
            },
            {
              value: "21%",
              text: "의 치명적인 사고가 졸음운전과 관련된 것으로 추정됩니다.",
            },
            { value: "109,000", text: "건의 부상 사고가 보고되었습니다." },
            {
              value: "328,000",
              text: "건의 사고가 매년 발생하는 것으로 추정됩니다.",
            },
            {
              value: "6,400",
              text: "건의 치명적 사고가 졸음운전과 관련이 있습니다.",
            },
          ],
          statsSource: "출처: AAA Foundation for Public Safety (2014. 11)",
          mountedTitle: "마운트 위치",
          mountedPhotos: [
            "스티어링 칼럼 커버 상단 (권장)",
            "계기판 하단 전방",
            "계기판 상단 전방",
            "대시보드 상단",
          ],
          partsTitle: "부품명/기능",
          parts: [
            {
              label: "동작 상태 LED:",
              text: [
                "운전자의 졸음 상태, 시선 이탈, 얼굴 인식 범위 초과 등 상태를 표시합니다.",
                "(녹색/파란색/빨간색)",
              ],
            },
            {
              label: "카메라 렌즈:",
              text: ["운전자의 실시간 상태를 모니터링합니다."],
            },
            {
              label: "GPS 상태 LED:",
              text: [
                "GPS 신호 상태를 표시합니다.",
                "(옵션 GPS 케이블 연결 시 사용 가능)",
                "- 하양 LED ON: GPS 신호 로그온 준비 완료",
                "- 하양 LED 깜빡임: 대기 중, GPS 연결 대기",
                "- 하양 LED OFF: GPS 신호 없음",
              ],
            },
            {
              label: "적외선 LED:",
              text: ["야간·터널 주행 시 운전자의 얼굴 감지용 조명 장치입니다."],
            },
            {
              label: "기능 스위치:",
              text: [
                "볼륨과 감도 설정을 제어합니다.",
                "- 짧은 누름: 볼륨 제어(뮤트/1~4단계)",
                "- 긴 누름: 감도 제어(상:레벨3 중:레벨2 하:레벨1)",
              ],
            },
            {
              label: "USB 포트:",
              text: [
                "경고등/진동기 등 각종 USB 액세서리 연결과",
                "펌웨어 업데이트를 지원합니다.",
              ],
            },
            {
              label: "I/O 포트:",
              text: [
                "외부 GPS 또는 차량 내비게이션 등 주변 장치와 연동합니다.",
              ],
            },
            {
              label: "Power Socket:",
              text: ["차량 시가잭(DC12/24V) 전원 연결용 포트입니다."],
            },
          ],
          title01: "권장 장착 위치",
          title02: "감지 범위",
        },
        telematics: {
          title: "텔레매틱스",
          introParagraph: [
            "최신 텔레매틱스 기술은 이러한 기능을 더 확장하고 있습니다.",
            "5G와 V2X 연결은 훨씬 빠르고 안정적인 데이터 교환을 가능하게 합니다.",
            "향후 차량과 사물(V2X) 간 통신 및 스마트시티 인프라가 결합되면",
            "차량은 교통 체계, 도로망, 타 차량과 동적으로 상호 작용하게 됩니다.",
            "이는 더 안전하고 친환경적이며 효율적인 교통 시스템을 위한 기반이 됩니다.",
            "나아가 반자율 및 자율주행 차량 운영의 기반도 마련합니다.",
          ],
          detailParagraphs: [
            [
              "조금 더 구체적으로는 운전자가 무선 네트워크로 차량을 원격 진단하고,",
              "교통·생활 정보, 주행 안정성, 차량 관리 등 관련 서비스를 이용할 수 있습니다.",
              "응급상황 감지와 긴급 조치도 원격으로 처리할 수 있습니다.",
              "즉 운전자는 차량과 네트워크로 연결돼 차량 상태를 언제든지",
              "원격으로 확인하고 관리할 수 있게 됩니다.",
            ],
            [
              "텔레매틱스 서비스는 차량 제조사와 이동통신사가 함께 협력해",
              "제공되는 형태가 일반적입니다. 통신 기술 관점에서 텔레매틱스는",
              "현재 4G LTE 기반에서 5G로 전환되고 있으며, 이는 자율주행에도",
              "직접적인 영향을 줍니다.",
            ],
            [
              "자동차 기술 측면에서도 성능 위주 개발에서 벗어나",
              "사용 편의성과 활용성을 강화하는 방향으로 진화하고 있습니다.",
            ],
          ],
          solutionTitle: "Eplus 솔루션",
          solution: {
            title: "QUECTEL",
            link: "https://www.quectel.com",
            text: [
              "Quectel은 상업용 텔레매틱스에 적합한 고내구성, 다기능 모듈로",
              "광범위한 상용 모델군을 보유하고 있습니다.",
            ],
          },
        },
      },
    },
  },
};
